import React, { useContext, useEffect, useState } from "react";
import FilterFieldSearchResult from "../FilterFieldSearchResult/FilterFieldSearchResult";
import { getFieldValueOptions } from "../../../../../Findings/FindingsFilters/FilterRow/FilterRowUtils";
import useFilterFieldValues from "../../../../../Findings/ManualFinding/FilterDataField/useFilterFieldValues";
import {
  MAX_RESULTS_TO_SHOW,
  MIN_CHARS_FOR_FILTER_QUERY,
} from "../../../../../Findings/FindingsFilters/FilterUtils";
import { GeneralAppContext } from "../../../../../General/GeneralAppProvider";
import type { ThemeName } from "../../../../../types/GeneralAppProvider";
import type { MenuState, MenuAction } from "../FilterFieldsSearch";
import type { CheckBoxListOption } from "../../../../../Forms/Controls/CheckBoxList/CheckBoxList";

// Canonical value-option shape produced by the untyped `getFieldValueOptions`
// util and forwarded to `CheckBoxList`. Includes the `CheckBoxListOption` index
// signature so it can be passed straight through without a cast.
export interface FilterFieldValueOption extends CheckBoxListOption {
  label: React.ReactNode;
  value: string[];
  leftImg?: string | null;
  leftIcon?: string | null;
}

export interface FilterFieldSearchQueryProps {
  resourceName: string;
  resourceType: string;
  onShowMoreClick: (resourceType: string) => void;
  resourceFullView: string | null;
  resultsCount: number;
  onCheckOption: (field: string, values: string[]) => void;
  selectedValues: string[];
  menu: MenuState;
  setMenu: (newState: MenuAction) => void;
  searchableFields: string[];
  resourceLabel?: string;
}

interface FilterFieldQueryData {
  // The EAV searcher is values-only, so the count falls back to values.length.
  total_count?: number | null;
  // `finding_filter_data.values` is a GraphQL scalar (runtime: array of filter
  // values on this no-coverage path). Held as `unknown[]` and forwarded to the
  // untyped `getFieldValueOptions`, which maps over it.
  values?: unknown[];
}

const FilterFieldSearchQuery: React.FC<FilterFieldSearchQueryProps> = ({
  resourceName,
  resourceType,
  onShowMoreClick,
  resourceFullView,
  resultsCount,
  onCheckOption,
  selectedValues,
  menu,
  setMenu,
  searchableFields,
  resourceLabel,
}) => {
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [valueOptions, setValueOptions] = useState<FilterFieldValueOption[]>(
    []
  );
  // `currentTheme` is read off the context object directly (runtime-undefined
  // today); the typed context exposes it under `appContext`. Cast preserves the
  // existing runtime read without widening the shared context type.
  const { currentTheme } = useContext(GeneralAppContext) as unknown as {
    currentTheme?: ThemeName;
  };

  const { setValueSearchInput, fieldOptions, loading, valueSearchInput } =
    useFilterFieldValues(
      resourceType,
      false,
      MIN_CHARS_FOR_FILTER_QUERY,
      false,
      resultsCount,
      true,
      "no-cache"
    );

  useEffect(() => {
    const resourceLoading = menu?.meta?.includes(resourceType);

    if (loading && !resourceLoading) {
      setMenu({ meta: [...menu?.meta, resourceType] });
    } else if (!loading && resourceLoading) {
      setMenu({ meta: menu?.meta.filter((item) => item !== resourceType) });
    }
  }, [loading, resourceType, valueSearchInput, menu?.meta]);

  useEffect(() => {
    setValueSearchInput(resourceName);
  }, [resourceName]);

  useEffect(() => {
    if (!!fieldOptions) {
      const queryData: FilterFieldQueryData = {
        total_count: fieldOptions?.finding_filter_data?.total_count,
        values: fieldOptions?.finding_filter_data?.values as
          | unknown[]
          | undefined,
      };

      const resolvedCount =
        queryData?.total_count ?? queryData?.values?.length ?? 0;

      setTotalCount(resolvedCount);

      if (resolvedCount > 0 && !menu?.sections?.includes(resourceType)) {
        const sections = [...menu?.sections, resourceType];
        const sortedSections = sections.sort(
          (a, b) => searchableFields.indexOf(a) - searchableFields.indexOf(b)
        );
        setMenu({ sections: sortedSections });
      } else if (
        resolvedCount === 0 &&
        menu?.sections?.includes(resourceType)
      ) {
        const sections = menu?.sections.filter((item) => item !== resourceType);
        const sortedSections = sections.sort(
          (a, b) => searchableFields.indexOf(a) - searchableFields.indexOf(b)
        );
        setMenu({ sections: sortedSections });
      }

      const res = getFieldValueOptions(
        resourceType,
        queryData?.values,
        currentTheme
      ) as FilterFieldValueOption[];

      setValueOptions(
        !!menu?.activeField ? res : res.splice(0, MAX_RESULTS_TO_SHOW)
      );
    }
  }, [
    fieldOptions,
    resourceType,
    valueSearchInput,
    menu?.sections,
    menu?.activeField,
  ]);

  return (
    <>
      {(totalCount ?? 0) > 0 && !loading && (
        <FilterFieldSearchResult
          resourceType={resourceType}
          resourceLabel={resourceLabel}
          totalCount={totalCount ?? 0}
          loadValues={loading}
          valueOptions={valueOptions}
          onCheckOption={(field, values) =>
            // `CheckBoxListValue` is the looser array the leaf emits; the parent
            // callback only ever receives string values here.
            onCheckOption(field, values as string[])
          }
          selectedValues={selectedValues}
          resourceFullView={!!resourceFullView}
          onShowMoreClick={onShowMoreClick}
          menu={menu}
          setMenu={setMenu}
        />
      )}
    </>
  );
};

export default FilterFieldSearchQuery;
